// src/AdminOverviewTab.js
import React, { useState, useEffect } from 'react';
import { authedFetch } from './utils/authedFetch';
import { apiUrl } from './utils/apiBase';

const AdminOverviewTab = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchOverviewMetrics();
  }, []);

  const fetchOverviewMetrics = async () => {
    try {
      setLoading(true);
      const response = await authedFetch(`${apiUrl('adminAnalytics')}?type=overview`);
      const result = await response.json();
      
      if (result.success) {
        setMetrics(result.data);
        setLastUpdated(new Date(result.timestamp));
        setError(null);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to fetch analytics data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={loadingStyle}>Loading analytics...</div>;
  }

  if (error) {
    return (
      <div style={errorStyle}>
        <p>Error: {error}</p>
        <button onClick={fetchOverviewMetrics} style={refreshButtonStyle}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h2>Overview Dashboard</h2>
        <div style={refreshContainerStyle}>
          <button onClick={fetchOverviewMetrics} style={refreshButtonStyle}>
            Refresh Data
          </button>
          {lastUpdated && (
            <span style={timestampStyle}>
              Last updated: {lastUpdated.toLocaleString()}
            </span>
          )}
        </div>
      </div>

      <div style={metricsGridStyle}>
        <div style={metricCardStyle}>
          <h3 style={metricTitleStyle}>Active Members</h3>
          <div style={metricValueStyle}>{metrics.totalActiveMembers}</div>
        </div>

        <div style={metricCardStyle}>
          <h3 style={metricTitleStyle}>New Signups (24h)</h3>
          <div style={metricValueStyle}>{metrics.newSignups24h}</div>
        </div>

        <div style={metricCardStyle}>
          <h3 style={metricTitleStyle}>New Signups (7d)</h3>
          <div style={metricValueStyle}>{metrics.newSignups7d}</div>
        </div>

        <div style={metricCardStyle}>
          <h3 style={metricTitleStyle}>New Signups (30d)</h3>
          <div style={metricValueStyle}>{metrics.newSignups30d}</div>
        </div>

        <div style={metricCardStyle}>
          <h3 style={metricTitleStyle}>Monthly Recurring Revenue</h3>
          <div style={metricValueStyle}>${metrics.monthlyRecurringRevenue}</div>
        </div>

        <div style={metricCardStyle}>
          <h3 style={metricTitleStyle}>Churn Rate</h3>
          <div style={metricValueStyle}>{metrics.churnRate}%</div>
        </div>
      </div>

      <div style={planBreakdownStyle}>
        <h3>Plan Distribution</h3>
        <div style={planGridStyle}>
          <div style={planCardStyle}>
            <div style={planLabelStyle}>Monthly Plan</div>
            <div style={planValueStyle}>{metrics.monthlyPlanCount}</div>
            <div style={planPercentStyle}>
              {Math.round((metrics.monthlyPlanCount / (metrics.monthlyPlanCount + metrics.annualPlanCount)) * 100)}%
            </div>
          </div>
          <div style={planCardStyle}>
            <div style={planLabelStyle}>Annual Plan</div>
            <div style={planValueStyle}>{metrics.annualPlanCount}</div>
            <div style={planPercentStyle}>
              {Math.round((metrics.annualPlanCount / (metrics.monthlyPlanCount + metrics.annualPlanCount)) * 100)}%
            </div>
          </div>
        </div>
      </div>

      <div style={renewalBreakdownStyle}>
        <h3>Auto-Renewal Settings</h3>
        <div style={renewalGridStyle}>
          <div style={renewalItemStyle}>
            <span>Auto-Renewal Enabled:</span>
            <span style={renewalValueStyle}>{metrics.autoRenewEnabled}</span>
          </div>
          <div style={renewalItemStyle}>
            <span>Auto-Renewal Disabled:</span>
            <span style={renewalValueStyle}>{metrics.autoRenewDisabled}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Styles
const containerStyle = {
  padding: '2rem',
  maxWidth: '1200px',
  margin: '0 auto'
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '2rem',
  paddingBottom: '1rem',
  borderBottom: '2px solid #e5e7eb'
};

const refreshContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '1rem'
};

const refreshButtonStyle = {
  padding: '0.5rem 1rem',
  backgroundColor: '#1976d2',
  color: 'white',
  border: 'none',
  borderRadius: '5px',
  cursor: 'pointer'
};

const timestampStyle = {
  fontSize: '0.9rem',
  color: '#666'
};

const metricsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  gap: '1.5rem',
  marginBottom: '3rem'
};

const metricCardStyle = {
  backgroundColor: 'white',
  padding: '1.5rem',
  borderRadius: '10px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  textAlign: 'center'
};

const metricTitleStyle = {
  margin: '0 0 1rem 0',
  fontSize: '0.9rem',
  fontWeight: '600',
  color: '#666',
  textTransform: 'uppercase'
};

const metricValueStyle = {
  fontSize: '2.5rem',
  fontWeight: 'bold',
  color: '#1976d2'
};

const planBreakdownStyle = {
  backgroundColor: 'white',
  padding: '2rem',
  borderRadius: '10px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  marginBottom: '2rem'
};

const planGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '2rem',
  marginTop: '1rem'
};

const planCardStyle = {
  textAlign: 'center',
  padding: '1rem'
};

const planLabelStyle = {
  fontSize: '1.1rem',
  fontWeight: '600',
  marginBottom: '0.5rem'
};

const planValueStyle = {
  fontSize: '2rem',
  fontWeight: 'bold',
  color: '#1976d2',
  marginBottom: '0.5rem'
};

const planPercentStyle = {
  fontSize: '1rem',
  color: '#666'
};

const renewalBreakdownStyle = {
  backgroundColor: 'white',
  padding: '2rem',
  borderRadius: '10px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
};

const renewalGridStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  marginTop: '1rem'
};

const renewalItemStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '0.75rem',
  backgroundColor: '#f8f9fa',
  borderRadius: '5px'
};

const renewalValueStyle = {
  fontWeight: 'bold',
  color: '#1976d2'
};

const loadingStyle = {
  padding: '2rem',
  textAlign: 'center',
  fontSize: '1.2rem'
};

const errorStyle = {
  padding: '2rem',
  textAlign: 'center',
  color: '#d32f2f'
};

export default AdminOverviewTab;